import { logger } from '../../../util/logger';

// Chrome can't decode HEIC/HEIF, so these images would fail to probe and render broken after upload.
// We decode them in the browser and upload a real JPEG instead — no backend change required.
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex !== -1 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
};

export const needsJpegConversion = (file: File): boolean => HEIC_EXTENSIONS.has(getFileExtension(file.name));

const toJpegFileName = (fileName: string): string => fileName.replace(/\.[^.]+$/, '.jpg');

// Best-effort: on any conversion failure we fall back to the original file so the upload still
// succeeds (it just won't render in Chrome). Conversion must never block the send.
export const convertToJpeg = async (file: File): Promise<File> => {
  if (!needsJpegConversion(file)) {
    return file;
  }
  try {
    // Lazy import: heic2any bundles a large libheif WASM payload that must stay out of the main
    // chunk and only load when a user actually attaches a HEIC/HEIF file.
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    return new File([blob], toJpegFileName(file.name), { type: 'image/jpeg' });
  } catch (error) {
    logger.error({
      message: 'HEIC/HEIF to JPEG conversion failed, uploading original file',
      error,
      callParams: { name: file.name },
    });
    return file;
  }
};
